import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Slider, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { styled } from "@mui/material/styles";

const AudioPlayerContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  backgroundColor: "#f5f5f5",
  borderRadius: theme.spacing(1.5),
  minWidth: "280px",
  maxWidth: "400px",
}));

const WaveformSlider = styled(Slider)(({ theme }) => ({
  color: "#7b1fa2",
  height: 4,
  "& .MuiSlider-thumb": {
    width: 12,
    height: 12,
    "&:hover, &.Mui-focusVisible": {
      boxShadow: "0 0 0 8px rgba(123, 31, 162, 0.16)",
    },
  },
  "& .MuiSlider-rail": {
    opacity: 0.3,
  },
}));

interface AudioPlayerProps {
  audioUrl: string;
  isOwnMessage?: boolean;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  audioUrl,
  isOwnMessage = false,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    console.log("🎵 AudioPlayer mounted with URL:", audioUrl);
    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    const handleLoadedMetadata = () => {
      console.log("✅ Audio metadata loaded, duration:", audio.duration);
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      if (!isDragging) {
        setCurrentTime(audio.currentTime);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleError = (e: Event) => {
      console.error("❌ Error loading audio:", e);
      console.error("   Audio URL:", audioUrl);
      console.error("   Error code:", (e.target as HTMLAudioElement)?.error?.code);
      console.error("   Error message:", (e.target as HTMLAudioElement)?.error?.message);
      setHasError(true);
    };

    setHasError(false);

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
      audio.pause();
      audio.src = "";
    };
  }, [audioUrl, isDragging]);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    if (audioRef.current) {
      const time = newValue as number;
      setCurrentTime(time);
      audioRef.current.currentTime = time;
    }
  };

  const handleSliderChangeStart = () => {
    setIsDragging(true);
  };

  const handleSliderChangeEnd = () => {
    setIsDragging(false);
  };

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (hasError) {
    return (
      <AudioPlayerContainer
        sx={{
          backgroundColor: "#ffebee",
        }}
      >
        <Typography variant="caption" color="error">
          Erro ao carregar áudio
        </Typography>
      </AudioPlayerContainer>
    );
  }

  return (
    <AudioPlayerContainer
      sx={{
        backgroundColor: isOwnMessage ? "#e3f2fd" : "#f5f5f5",
      }}
    >
      <IconButton
        size="small"
        onClick={togglePlay}
        sx={{
          bgcolor: "#7b1fa2",
          color: "white",
          "&:hover": { bgcolor: "#6a1b9a" },
        }}
      >
        {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
      </IconButton>
      <Box sx={{ flex: 1 }}>
        <WaveformSlider
          size="small"
          value={currentTime}
          max={duration || 100}
          onChange={handleSliderChange}
          onMouseDown={handleSliderChangeStart}
          onMouseUp={handleSliderChangeEnd}
          onTouchStart={handleSliderChangeStart}
          onTouchEnd={handleSliderChangeEnd}
        />
      </Box>
      <Typography variant="caption" sx={{ minWidth: "40px", color: "#666" }}>
        {formatTime(duration - currentTime)}
      </Typography>
    </AudioPlayerContainer>
  );
};

export default AudioPlayer;
